/**
 * AdCreativeStudio — Legion-only
 * Renders three 9:16 Instagram story ad card variations using Canvas.
 * Uses brand fonts (BaronessKuffner, AmericanCaptain) and exact brand colours.
 * Each card exports as a full-resolution PNG (1080×1920).
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import { X, Download, RefreshCw } from 'lucide-react';
import { useUserData } from './UserDataContext';

// ── Dimensions ────────────────────────────────────────────────────────────────
const EXPORT_W = 1080;
const EXPORT_H = 1920;
const PREVIEW_SCALE = 0.25; // 270 × 480 preview

// ── Brand palette ─────────────────────────────────────────────────────────────
const C = {
  orange:    '#F39C12',
  orangeRed: '#E74C3C',
  blue:      '#4478ea',
  deepBlue:  '#2d1ef1',
  darkBg:    '#0D0D0D',
  navy:      '#2C3E50',
  gold:      '#F39C12',
  white:     '#FFFFFF',
  offWhite:  '#E5E5E5',
  purple:    '#5a2d82',
  green:     '#2d6a30',
};

// ── Font loader (same pattern as ShareProfileModal) ────────────────────────────
let _fontsReady = false;
async function loadFonts(): Promise<void> {
  if (_fontsReady) return;
  await Promise.allSettled([
    new FontFace('BaronessKuffner', 'url("/shareprofile/BaronessKuffner.ttf")').load()
      .then(f => (document.fonts as FontFaceSet).add(f)),
    new FontFace('AmericanCaptain', 'url("/shareprofile/AmericanCaptain.ttf")').load()
      .then(f => (document.fonts as FontFaceSet).add(f)),
  ]);
  _fontsReady = true;
}

// ── Image loader helper ────────────────────────────────────────────────────────
function loadImg(src: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload  = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
}

// ── Canvas text helpers ────────────────────────────────────────────────────────
function wrapText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxW: number, lineH: number) {
  const words = text.split(' ');
  let line = '';
  let cy = y;
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxW && line) {
      ctx.fillText(line, x, cy);
      line = word;
      cy += lineH;
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, cy);
  return cy + lineH;
}

function pill(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number, fill: string) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
}

// ── Draw functions ─────────────────────────────────────────────────────────────

async function drawDiscover(canvas: HTMLCanvasElement, logo: HTMLImageElement | null) {
  const ctx = canvas.getContext('2d')!;
  const W = canvas.width, H = canvas.height;

  // Background: dark → blue radial glow
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#060c1a');
  bg.addColorStop(0.5, '#0a1a3a');
  bg.addColorStop(1, '#0D0D0D');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Blue glow blob centre
  const glow = ctx.createRadialGradient(W * 0.5, H * 0.38, 0, W * 0.5, H * 0.38, W * 0.65);
  glow.addColorStop(0, 'rgba(68,120,234,0.28)');
  glow.addColorStop(1, 'rgba(68,120,234,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // ── Logo ──
  if (logo) {
    const lw = 260, lh = (logo.height / logo.width) * lw;
    ctx.drawImage(logo, (W - lw) / 2, 100, lw, lh);
  }

  // ── Mock swipe card ──
  const cx = W / 2, cardW = W * 0.72, cardH = H * 0.38;
  const cardX = cx - cardW / 2, cardY = H * 0.19;

  // Card shadow
  ctx.shadowColor = 'rgba(0,0,0,0.7)'; ctx.shadowBlur = 60; ctx.shadowOffsetY = 20;
  pill(ctx, cardX, cardY, cardW, cardH, 36, '#151e30');
  ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;

  // Card gradient fill
  const cardGrad = ctx.createLinearGradient(cardX, cardY, cardX, cardY + cardH);
  cardGrad.addColorStop(0, '#1a2540');
  cardGrad.addColorStop(1, '#0e1526');
  pill(ctx, cardX, cardY, cardW, cardH, 36, '#1a2540'); // base
  ctx.save(); ctx.beginPath();
  ctx.moveTo(cardX + 36, cardY); ctx.arcTo(cardX + cardW, cardY, cardX + cardW, cardY + 36, 36);
  ctx.arcTo(cardX + cardW, cardY + cardH, cardX + cardW - 36, cardY + cardH, 36);
  ctx.arcTo(cardX, cardY + cardH, cardX, cardY + cardH - 36, 36);
  ctx.arcTo(cardX, cardY, cardX + 36, cardY, 36);
  ctx.closePath(); ctx.fillStyle = cardGrad; ctx.fill(); ctx.restore();

  // Flag badge (🇬🇭 Ghana placeholder)
  ctx.font = `${Math.round(W * 0.08)}px serif`;
  ctx.textAlign = 'center';
  ctx.fillText('🇬🇭', cardX + cardW - 50, cardY + 70);

  // Name + age
  ctx.fillStyle = C.white;
  ctx.font = `bold ${Math.round(W * 0.055)}px BaronessKuffner, Poppins, sans-serif`;
  ctx.textAlign = 'left';
  ctx.fillText('Amara · 24', cardX + 44, cardY + cardH * 0.52);

  ctx.fillStyle = C.offWhite;
  ctx.font = `${Math.round(W * 0.032)}px sans-serif`;
  ctx.fillText('@amara_gh · London, UK', cardX + 44, cardY + cardH * 0.62);

  // Identity tags
  const tags = ['Pan-African', 'Afrobeats', 'Creative'];
  let tx = cardX + 44;
  tags.forEach(tag => {
    const tw = ctx.measureText(tag).width + 32;
    pill(ctx, tx, cardY + cardH * 0.70, tw, 38, 19, 'rgba(68,120,234,0.35)');
    ctx.fillStyle = '#a8c4ff';
    ctx.font = `${Math.round(W * 0.028)}px sans-serif`;
    ctx.fillText(tag, tx + 16, cardY + cardH * 0.70 + 26);
    tx += tw + 16;
  });

  // Swipe icons
  ctx.font = `${Math.round(W * 0.068)}px serif`;
  ctx.textAlign = 'center';
  ctx.fillText('✕', cardX + 90, cardY + cardH + 80);
  ctx.fillStyle = C.orange; ctx.fillText('♥', cardX + cardW - 90, cardY + cardH + 80);

  // ── Headline ──
  ctx.fillStyle = C.white;
  ctx.font = `bold ${Math.round(W * 0.12)}px BaronessKuffner, Poppins, sans-serif`;
  ctx.textAlign = 'center';
  const headY = cardY + cardH + 160;
  wrapText(ctx, 'YOUR PEOPLE ARE OUT THERE', W / 2, headY, W * 0.86, Math.round(W * 0.14));

  // Sub
  ctx.fillStyle = C.offWhite;
  ctx.font = `${Math.round(W * 0.042)}px AmericanCaptain, sans-serif`;
  const subY = headY + 280;
  wrapText(ctx, 'Discover African & Caribbean diaspora from Lagos to London', W / 2, subY, W * 0.78, Math.round(W * 0.055));

  // CTA button
  const btnW = W * 0.72, btnH = 110, btnX = (W - btnW) / 2, btnY = H - 340;
  pill(ctx, btnX, btnY, btnW, btnH, 55, C.orange);
  ctx.fillStyle = C.white;
  ctx.font = `bold ${Math.round(W * 0.048)}px AmericanCaptain, Poppins, sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('DOWNLOAD AFROYAP FREE', W / 2, btnY + 72);

  // Footer
  ctx.fillStyle = 'rgba(255,255,255,0.35)';
  ctx.font = `${Math.round(W * 0.030)}px sans-serif`;
  ctx.fillText('Search "AfroYap" on the App Store', W / 2, H - 180);
}

async function drawTribe(canvas: HTMLCanvasElement, logo: HTMLImageElement | null) {
  const ctx = canvas.getContext('2d')!;
  const W = canvas.width, H = canvas.height;

  // Background: deep purple → near-black
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#1a0a30');
  bg.addColorStop(0.6, '#0d0520');
  bg.addColorStop(1, '#0D0D0D');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Purple glow
  const glow = ctx.createRadialGradient(W * 0.5, H * 0.35, 0, W * 0.5, H * 0.35, W * 0.7);
  glow.addColorStop(0, 'rgba(90,45,130,0.4)');
  glow.addColorStop(1, 'rgba(90,45,130,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // ── Logo ──
  if (logo) {
    const lw = 240, lh = (logo.height / logo.width) * lw;
    ctx.drawImage(logo, (W - lw) / 2, 90, lw, lh);
  }

  // ── Tribe bubbles ──
  const tribes = [
    { label: 'Yoruba Culture', emoji: '🌍', color: '#5a2d82' },
    { label: 'Afrobeats',      emoji: '🎵', color: '#2d6a30' },
    { label: 'Caribbean',      emoji: '🏝️', color: '#1a3a7a' },
    { label: 'UK Diaspora',    emoji: '🇬🇧', color: '#7a1a1a' },
    { label: 'Pan-African',    emoji: '✊🏿', color: '#2d4a1a' },
    { label: 'History & Roots',emoji: '📖', color: '#4a2d00' },
  ];

  const cols = 3, rows = 2;
  const bW = W * 0.26, bH = W * 0.28;
  const gapX = (W - cols * bW) / (cols + 1);
  const gapY = 40;
  const startY = H * 0.22;

  tribes.forEach((tribe, i) => {
    const col = i % cols, row = Math.floor(i / cols);
    const bx = gapX + col * (bW + gapX);
    const by = startY + row * (bH + gapY);
    const r = bW / 2;

    // Circle
    ctx.shadowColor = 'rgba(0,0,0,0.5)'; ctx.shadowBlur = 30;
    ctx.beginPath();
    ctx.arc(bx + r, by + r, r, 0, Math.PI * 2);
    const cg = ctx.createRadialGradient(bx + r, by + r, 0, bx + r, by + r, r);
    cg.addColorStop(0, tribe.color + 'cc');
    cg.addColorStop(1, tribe.color + '44');
    ctx.fillStyle = cg;
    ctx.fill();
    ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0;

    // Emoji
    ctx.font = `${Math.round(r * 0.65)}px serif`;
    ctx.textAlign = 'center';
    ctx.fillText(tribe.emoji, bx + r, by + r + Math.round(r * 0.22));

    // Label below
    ctx.fillStyle = C.offWhite;
    ctx.font = `${Math.round(W * 0.028)}px sans-serif`;
    ctx.fillText(tribe.label, bx + r, by + bH + 30);
  });

  // ── Headline ──
  const headY = startY + rows * (bH + gapY) + 100;
  ctx.fillStyle = C.white;
  ctx.font = `bold ${Math.round(W * 0.11)}px BaronessKuffner, Poppins, sans-serif`;
  ctx.textAlign = 'center';
  wrapText(ctx, 'FIND YOUR TRIBE', W / 2, headY, W * 0.85, Math.round(W * 0.135));

  // Sub
  ctx.fillStyle = C.offWhite;
  ctx.font = `${Math.round(W * 0.040)}px AmericanCaptain, sans-serif`;
  const subY = headY + 200;
  wrapText(ctx, 'African & Caribbean communities in one place', W / 2, subY, W * 0.78, Math.round(W * 0.052));

  // CTA
  const btnW = W * 0.72, btnH = 110, btnX = (W - btnW) / 2, btnY = H - 340;
  pill(ctx, btnX, btnY, btnW, btnH, 55, '#5a2d82');
  ctx.fillStyle = C.white;
  ctx.font = `bold ${Math.round(W * 0.044)}px AmericanCaptain, Poppins, sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('JOIN THE CONVERSATION', W / 2, btnY + 72);

  ctx.fillStyle = 'rgba(255,255,255,0.3)';
  ctx.font = `${Math.round(W * 0.028)}px sans-serif`;
  ctx.fillText('Search "AfroYap" on the App Store', W / 2, H - 180);
}

async function drawDiaspora(canvas: HTMLCanvasElement, logo: HTMLImageElement | null) {
  const ctx = canvas.getContext('2d')!;
  const W = canvas.width, H = canvas.height;

  // Warm gradient: gold → red → dark navy
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0.0, '#c47a00');
  bg.addColorStop(0.35, '#b03a1a');
  bg.addColorStop(0.7, '#4a1a00');
  bg.addColorStop(1.0, '#0D0D0D');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Warm glow
  const glow = ctx.createRadialGradient(W * 0.5, H * 0.3, 0, W * 0.5, H * 0.3, W * 0.7);
  glow.addColorStop(0, 'rgba(243,156,18,0.25)');
  glow.addColorStop(1, 'rgba(243,156,18,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // ── Logo (dark variant since bg is warm) ──
  if (logo) {
    const lw = 240, lh = (logo.height / logo.width) * lw;
    // Darken logo area slightly so white logo pops
    ctx.fillStyle = 'rgba(0,0,0,0.2)';
    ctx.fillRect((W - lw) / 2 - 20, 80, lw + 40, lh + 20);
    ctx.drawImage(logo, (W - lw) / 2, 90, lw, lh);
  }

  // ── City typography grid ──
  const cityPairs = [
    ['Lagos', 'London'],
    ['Atlanta', 'Toronto'],
    ['Paris', 'Accra'],
    ['Birmingham', 'New York'],
  ];

  const startY = H * 0.24;
  const lineH = H * 0.095;

  cityPairs.forEach(([left, right], i) => {
    const y = startY + i * lineH;
    ctx.textAlign = 'center';

    // Alternating opacity for visual rhythm
    const alpha = i === 0 ? 1 : i === 1 ? 0.85 : i === 2 ? 0.65 : 0.45;
    const size = i === 0
      ? Math.round(W * 0.095)
      : i === 1 ? Math.round(W * 0.075)
      : i === 2 ? Math.round(W * 0.060)
      : Math.round(W * 0.048);

    ctx.fillStyle = `rgba(255,255,255,${alpha})`;
    ctx.font = `bold ${size}px BaronessKuffner, Poppins, sans-serif`;
    ctx.fillText(`${left}  ·  ${right}`, W / 2, y);
  });

  // Divider line
  const divY = startY + cityPairs.length * lineH + 30;
  ctx.strokeStyle = 'rgba(255,255,255,0.3)';
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(W * 0.15, divY); ctx.lineTo(W * 0.85, divY); ctx.stroke();

  // ── Headline ──
  const headY = divY + 80;
  ctx.fillStyle = C.white;
  ctx.font = `bold ${Math.round(W * 0.095)}px BaronessKuffner, Poppins, sans-serif`;
  ctx.textAlign = 'center';
  wrapText(ctx, 'HOME IS WHERE YOUR PEOPLE ARE', W / 2, headY, W * 0.85, Math.round(W * 0.115));

  // Sub
  ctx.fillStyle = 'rgba(255,255,255,0.82)';
  ctx.font = `${Math.round(W * 0.038)}px AmericanCaptain, sans-serif`;
  const subY = headY + 310;
  wrapText(ctx, 'The app connecting the African & Caribbean diaspora worldwide', W / 2, subY, W * 0.76, Math.round(W * 0.050));

  // CTA button
  const btnW = W * 0.72, btnH = 110, btnX = (W - btnW) / 2, btnY = H - 340;
  pill(ctx, btnX, btnY, btnW, btnH, 55, C.white);
  ctx.fillStyle = '#8a2000';
  ctx.font = `bold ${Math.round(W * 0.044)}px AmericanCaptain, Poppins, sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('START CONNECTING TODAY', W / 2, btnY + 72);

  ctx.fillStyle = 'rgba(255,255,255,0.45)';
  ctx.font = `${Math.round(W * 0.028)}px sans-serif`;
  ctx.fillText('Search "AfroYap" on the App Store', W / 2, H - 180);
}

// ── Card component ─────────────────────────────────────────────────────────────
const variants: {
  id: string;
  label: string;
  tag: string;
  draw: (c: HTMLCanvasElement, logo: HTMLImageElement | null) => Promise<void>;
}[] = [
  { id: 'discover',  label: 'Discover Hook',     tag: 'Your people are out there',    draw: drawDiscover },
  { id: 'tribe',     label: 'Tribe / Community', tag: 'Find your tribe',              draw: drawTribe    },
  { id: 'diaspora',  label: 'Lagos → London',    tag: 'Home is where your people are',draw: drawDiaspora },
];

function AdCard({ variant, logo }: { variant: typeof variants[0]; logo: HTMLImageElement | null }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [rendering, setRendering] = useState(true);

  const PW = Math.round(EXPORT_W * PREVIEW_SCALE);
  const PH = Math.round(EXPORT_H * PREVIEW_SCALE);

  const render = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width  = EXPORT_W;
    canvas.height = EXPORT_H;
    setRendering(true);
    await variant.draw(canvas, logo);
    setRendering(false);
  };

  useEffect(() => { render(); }, [logo]);

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `afroyap-ad-${variant.id}-1080x1920.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative rounded-xl overflow-hidden border border-white/10 shadow-lg"
           style={{ width: PW, height: PH }}>
        <canvas
          ref={canvasRef}
          style={{ width: PW, height: PH, display: 'block' }}
        />
        {rendering && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <RefreshCw className="w-6 h-6 text-white animate-spin" />
          </div>
        )}
      </div>

      <div className="text-center w-full" style={{ maxWidth: PW }}>
        <p className="text-white text-sm font-bold mb-0.5">{variant.label}</p>
        <p className="text-zinc-500 text-[11px] mb-3 italic">"{variant.tag}"</p>
        <button
          onClick={download}
          disabled={rendering}
          className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-xs font-semibold bg-white/8 hover:bg-white/15 text-zinc-200 border border-white/10 transition-colors disabled:opacity-40"
        >
          <Download className="w-3.5 h-3.5" />
          Export 1080 × 1920 PNG
        </button>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export function AdCreativeStudio({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { userProfile } = useUserData();
  const [logo, setLogo] = useState<HTMLImageElement | null>(null);
  const [ready, setReady] = useState(false);

  const isAdmin = userProfile?.rank === 'legion' || userProfile?.rank === 'watcher';

  useEffect(() => {
    if (!isOpen || !isAdmin) return;
    (async () => {
      await loadFonts();
      try {
        const img = await loadImg('/shareprofile/afroyapper.png');
        setLogo(img);
      } catch {
        setLogo(null); // render without logo if missing
      }
      setReady(true);
    })();
  }, [isOpen, isAdmin]);

  if (!isOpen || !isAdmin) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10000002] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="w-full max-w-4xl rounded-2xl border border-white/10 bg-[#0D0D0D] shadow-2xl overflow-hidden"
        style={{ maxHeight: '92vh', overflowY: 'auto' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <h2 className="text-lg font-bold text-white">Ad Creative Studio</h2>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              3 × Instagram Story (9:16 · 1080×1920) — export as PNG
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-zinc-500 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!ready ? (
          <div className="flex items-center justify-center py-24">
            <div className="flex flex-col items-center gap-3 text-zinc-500">
              <RefreshCw className="w-8 h-8 animate-spin" />
              <p className="text-sm">Loading fonts & assets…</p>
            </div>
          </div>
        ) : (
          <>
            {/* Cards */}
            <div className="p-6 flex flex-wrap justify-center gap-8">
              {variants.map(v => (
                <motion.div
                  key={v.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  <AdCard variant={v} logo={logo} />
                </motion.div>
              ))}
            </div>

            {/* Copy brief */}
            <div className="px-6 pb-6 space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500">Creator Brief Notes</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-[11px] text-zinc-400">
                <div className="p-3 rounded-lg bg-white/5 border border-white/8">
                  <p className="font-semibold text-blue-400 mb-1">Variant 1 · Discover Hook</p>
                  <p>Placement: westafricancommunity interest. Hook: the swipe mechanic / social discovery angle. Target: 18–34, UK/US/CA. CTA objective: App Install.</p>
                </div>
                <div className="p-3 rounded-lg bg-white/5 border border-white/8">
                  <p className="font-semibold text-purple-400 mb-1">Variant 2 · Tribe Community</p>
                  <p>Placement: diaspora interest groups. Hook: belonging / community. Target: 20–40 who engage with African culture content. CTA: Community Join.</p>
                </div>
                <div className="p-3 rounded-lg bg-white/5 border border-white/8">
                  <p className="font-semibold text-orange-400 mb-1">Variant 3 · Lagos → London</p>
                  <p>Placement: geographic retargeting Lagos/London/Atlanta/Toronto. Hook: identity & place. Target: 22–38 first/second gen diaspora. CTA: App Install.</p>
                </div>
              </div>
            </div>
          </>
        )}
      </motion.div>
    </div>,
    document.body
  );
}
